import {
  animate,
  type MotionValue,
  motionValue,
  useMotionValue,
  useTransform,
} from 'motion/react';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';

import {
  TRACE_PEN_PIECES,
  type WorkHomeMarkTracePiece,
} from './work-home-mark-trace-path';

// Entrance motion for `WorkHomeMark` — plays once on every mount (initial load
// and every return to Work-home) when the surface renders the composer wave.
//
// THE DRAW. The mark is a stroked outline that DRAWS ITSELF IN as the wave
// rises: three concurrent pens (SVG `pathLength` 0 -> 1 on each piece —
// stroke-dasharray / dashoffset under the hood) trace the logo's contours from
// under the bottom fade and meet at the top centre, while the smaller lobe
// loops and rings flick in over the last three quarters of the same clock. The
// pens start TRACE_DELAY_S after the wave begins its rise (the sheet is
// visibly present by then) and finish together at TRACE_DURATION_S — each
// pen's speed is its own arc length over that one duration — on an ease-out,
// so the meeting reads as the strokes closing on each other. With the wave's
// hit at 460ms and its lock at 540ms, the outline completes at 750ms, shortly
// after the glow has settled under it. Nothing fades in behind the outline:
// no fill, no body. The stroke's look (a faint glass edge, see
// `TRACE_GLASS_EDGE` in work-home-mark-trace-render.tsx) is the same from the
// first drawn pixel to rest — the alphas are static attributes.
//
// THE RISE. Alongside the draw the whole mark rises TRACE_SETTLE_Y_PX on an
// ease-out-quart TWEEN of exactly the draw's duration — a tween, not a
// spring, so the mark is provably stationary the instant the stroke closes;
// nothing transforms after the draw completes. The draw completing is what
// settles the mark (`hasSettledRef`) and opens the pointer-follow glint.
//
// THE COLLAPSE. On a composer submit the finished outline pinches into
// nothing (see `useWorkHomeMarkEntrance`'s third argument): a uniform
// scale-down about its own centre with the width leading the height, on an
// ease-in, then hidden. If the submit signal drops while the mark is still
// mounted, the outline re-draws.
//
// Only transform (translateY, scaleX/scaleY), opacity and pathLength are
// animated, per the repo animation guide's performance/reduced-motion rules —
// Motion sets `will-change: transform` for the transforms itself. Under
// `prefers-reduced-motion` the outline renders drawn and at rest on the first
// paint, and the collapse hides/restores instantly.
export const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

// A dedicated, one-shot check rather than the library's `useReducedMotion` —
// that hook lazily initialises a single matchMedia listener shared by every
// motion component mounted in the process (see `initPrefersReducedMotion` in
// motion-dom), so a later mount cannot get its own fresh read once the first
// one has run. Missing `matchMedia` (older WebViews, non-browser renders) is
// treated the same as "reduce": animating transform without being able to
// confirm it's safe isn't a chance worth taking for a first-paint mark.
export const getPrefersReducedMotion = (): boolean =>
  typeof window === 'undefined' || typeof window.matchMedia !== 'function'
    ? true
    : window.matchMedia(REDUCED_MOTION_QUERY).matches;

/**
 * `mode` decides whether the wave-synced draw plays at all:
 *
 * - `'wave'` — the full entrance described above. ONLY for a surface that
 *   actually renders `WorkHomeComposerWave`, because the draw is timed off
 *   that wave's rise.
 * - `'immediate'` (the DEFAULT) — no delay, no draw, no rise: the outline is
 *   at its resting values on the very first paint and is settled at once, so
 *   pointer-follow and the ambient glint are live immediately. This is what
 *   `SessionWorkLanding` and `WorkHomeChrome` get, and it is the same state
 *   the reduced-motion path renders.
 *
 * Defaulting to `'immediate'` is deliberate: a new surface that mounts the
 * mark without a wave then behaves correctly by omission rather than
 * inheriting a wait on a wave it never renders.
 */
export type WorkHomeMarkEntranceMode = 'wave' | 'immediate';

// --- The draw ---------------------------------------------------------------
// 0.6s: quick enough that three strokes closing on each other read as one
// gesture rather than an illustration being built; under the guide's 1s cap.
export const TRACE_DURATION_S = 0.6;
// The lobe loops and rings ('flick' pieces) start here and run to the end, so
// nothing appears to wait for the pens.
export const TRACE_FLICK_START_FRACTION = 0.25;
// The pens start this long after the composer wave begins its rise: the sheet
// is visibly present by ~110ms (see WAVE_OPACITY_IN_FRACTION in the wave
// module), so the first stroke lands just after the light shows. Draw start
// 150ms, draw end 750ms, against the wave's hit at 460ms and lock at 540ms.
export const TRACE_DELAY_S = 0.15;
const TRACE_EASE_OUT_QUAD = [0.25, 0.46, 0.45, 0.94] as const; // animations.md ease-out-quad
const TRACE_EASE_OUT_QUART = [0.165, 0.84, 0.44, 1] as const; // animations.md ease-out-quart

// --- The rise ---------------------------------------------------------------
// Starts this far below rest and rises to it on ease-out-quart over exactly
// the draw's duration (~90% of the travel is done by half-time, so the mark is
// all but still while the last half of the stroke draws, and exactly still
// when it completes).
export const TRACE_SETTLE_Y_PX = 12;
export const TRACE_RISE_DURATION_S = TRACE_DURATION_S;

// --- The submit collapse ------------------------------------------------------
// Matched frame by frame to the reference clip: the outline stays WHOLE, scales
// down about its own CENTRE over ~570ms on ease-in-quart with scaleX leading
// scaleY (so it pinches horizontally into a dot), and opacity holds at 1 until
// the final ~10%, where a tiny fade stops a lingering 1px dot.
export const TRACE_COLLAPSE_DURATION_S = 0.57;
// scaleX leads scaleY: at 60% of the (eased) progress the width is already
// down to 0.3 while the height is still ~0.4; both end at a near-point.
export const TRACE_COLLAPSE_X_MID = 0.3;
export const TRACE_COLLAPSE_END_SCALE = 0.02;
export const TRACE_COLLAPSE_FADE_START = 0.9; // opacity holds at 1 until here
const TRACE_EASE_IN_QUART = [0.895, 0.03, 0.685, 0.22] as const; // animations.md ease-in-quart

// Each piece's [start, duration] on the shared draw clock, in seconds from
// the draw's start. A pen piece ('left' | 'right' | 'inner') runs the WHOLE
// clock — concurrent pens, each at its own length / duration speed, so they
// finish together at the meeting point. A 'flick' starts at
// TRACE_FLICK_START_FRACTION of it and also ends exactly at the end.
export const tracePieceSchedule = (
  pieces: readonly WorkHomeMarkTracePiece[] = TRACE_PEN_PIECES,
  durationS: number = TRACE_DURATION_S
): { startS: number; durationS: number }[] =>
  pieces.map((piece) =>
    piece.pen === 'flick'
      ? {
          startS: durationS * TRACE_FLICK_START_FRACTION,
          durationS: durationS * (1 - TRACE_FLICK_START_FRACTION),
        }
      : { startS: 0, durationS }
  );

// The draw, shared by the entrance and the post-collapse re-draw: every
// piece is scheduled on the one clock (by schedule rather than by chaining,
// so a dropped frame cannot open a gap); all of them end at the same
// instant, and the mark settles off the first pen's completion. Returns one
// stop for everything. ONE animation per piece: each piece's three strokes
// sit in a `<g>` that carries the piece's single `pathLength` (see
// `traceStrokePaths`), so a frame writes one dasharray per piece, not three.
const runTraceDraw = (
  pathLengths: readonly MotionValue<number>[],
  delayS: number,
  onSettled: () => void
): (() => void) => {
  const schedule = tracePieceSchedule();
  const draws = pathLengths.map((pathLength, index) => {
    const isFlick = TRACE_PEN_PIECES[index]?.pen === 'flick';
    return animate(pathLength, 1, {
      delay: delayS + schedule[index]!.startS,
      duration: schedule[index]!.durationS,
      ease: isFlick ? 'linear' : TRACE_EASE_OUT_QUAD,
      onComplete: index === 0 ? onSettled : undefined,
    });
  });
  return () => {
    for (const draw of draws) draw.stop();
  };
};

/** One pen piece and its single animated `pathLength` (shared by its three strokes). */
export interface WorkHomeMarkTraceStroke {
  piece: WorkHomeMarkTracePiece;
  pathLength: MotionValue<number>;
}

/**
 * Owns the mark's motion values and derives every animated quantity from
 * them.
 *
 * - `entranceMotionProps.style` spreads onto the mark's `m.svg` (the rise and
 *   the collapse transforms; `originX`/`originY` centre the collapse).
 * - `traceStrokes` is one entry per pen piece, each with the piece's animated
 *   `pathLength`, for `traceStrokePaths` to render.
 * - `hasSettledRef` is what the pointer-follow effect in `WorkHomeMark`
 *   gates on so the highlight can't engage mid-draw (or mid-collapse).
 * - `collapsed` is true once the submit collapse has finished: the svg hides
 *   itself (a class, not an unmount).
 *
 * `prefersReducedMotion` is read ONCE by the caller (`WorkHomeMark`) and
 * shared with the glint, rather than re-read here; it defaults to a fresh
 * read for any other caller. That snapshot governs the ENTRANCE, which plays
 * once at mount. The submit collapse (and its re-draw) can fire many times
 * within one long-lived mount, so those re-read the preference LIVE at the
 * moment they fire — someone who turns reduced motion on mid-session gets an
 * instant hide/restore on their next send, matching the wave
 * (`useReducedMotion`) and the glint (a `matchMedia` change listener).
 * `submitting` is the composer's submit signal.
 */
export const useWorkHomeMarkEntrance = (
  mode: WorkHomeMarkEntranceMode = 'immediate',
  prefersReducedMotion: boolean = getPrefersReducedMotion(),
  submitting = false
): {
  hasSettledRef: MutableRefObject<boolean>;
  /** One-shot read at mount; the ambient glint drift stays static when true. */
  prefersReducedMotion: boolean;
  collapsed: boolean;
  entranceMotionProps: {
    style: {
      y: MotionValue<string>;
      scaleX: MotionValue<number>;
      scaleY: MotionValue<number>;
      opacity: MotionValue<number>;
      originX: number;
      originY: number;
    };
  };
  traceStrokes: readonly WorkHomeMarkTraceStroke[];
} => {
  // Both reasons to skip the entrance collapse to the same rendered state —
  // every motion value already at its resting value, settled — so they share
  // one flag. `prefersReducedMotion` is still returned separately because the
  // ambient glint drift keys off the PREFERENCE, not off the entrance mode:
  // a wave-less surface still gets its glint.
  const skipEntrance = prefersReducedMotion || mode === 'immediate';
  const hasSettledRef = useRef(skipEntrance);
  // One pathLength per pen piece (a fixed, module-constant count, created
  // once — plain `motionValue`s in state rather than N hook calls). Skipping
  // the entrance: fully drawn (1) from the first paint.
  const [pathLengths] = useState(() =>
    TRACE_PEN_PIECES.map(() => motionValue(skipEntrance ? 1 : 0))
  );
  const riseProgress = useMotionValue(skipEntrance ? 1 : 0);
  const collapseProgress = useMotionValue(0);
  const [collapsed, setCollapsed] = useState(false);
  // Whether a collapse has PLAYED (as opposed to finished), so a dropped
  // signal re-draws only after a real collapse — never on first mount.
  const hasCollapsedRef = useRef(false);
  const delayS = mode === 'wave' ? TRACE_DELAY_S : 0;

  // Clamped (the default): the rise is a tween with a hard end, so there is
  // no overshoot to let through — the mark must sit EXACTLY at rest when the
  // stroke closes.
  const y = useTransform(riseProgress, [0, 1], [`${TRACE_SETTLE_Y_PX}px`, '0px']);
  // The collapse: X leads Y through the middle of the progress and both end
  // at a near-point; opacity flat until the last ~10%.
  const scaleX = useTransform(
    collapseProgress,
    [0, 0.6, 1],
    [1, TRACE_COLLAPSE_X_MID, TRACE_COLLAPSE_END_SCALE]
  );
  const scaleY = useTransform(collapseProgress, [0, 1], [1, TRACE_COLLAPSE_END_SCALE]);
  const opacity = useTransform(
    collapseProgress,
    [0, TRACE_COLLAPSE_FADE_START, 1],
    [1, 1, 0]
  );

  useEffect(() => {
    if (hasSettledRef.current) return;
    const markSettled = () => {
      hasSettledRef.current = true;
    };
    // The draw completing is what settles the mark and opens pointer-follow.
    const stopDraw = runTraceDraw(pathLengths, delayS, markSettled);
    // Same start, same length as the draw: the rise finishes exactly when
    // the stroke closes, so nothing transforms after the draw completes.
    const rise = animate(riseProgress, 1, {
      delay: delayS,
      duration: TRACE_RISE_DURATION_S,
      ease: TRACE_EASE_OUT_QUART,
    });
    // Belt and braces for environments whose animation frame loop does not
    // run (the settle must still open the pointer-follow gate).
    const settleTimer = window.setTimeout(
      markSettled,
      (delayS + TRACE_DURATION_S) * 1000
    );
    return () => {
      stopDraw();
      rise.stop();
      window.clearTimeout(settleTimer);
    };
  }, [pathLengths, riseProgress, delayS]);

  // The submit collapse, and the re-draw if the signal drops while still
  // mounted. Reduced motion is read LIVE here (not the mount snapshot): this
  // effect can run many times in one mount.
  useEffect(() => {
    if (submitting) {
      // The instant the send starts: close pointer-follow and the ambient
      // glint (both key off `hasSettledRef`), then fold.
      hasSettledRef.current = false;
      hasCollapsedRef.current = true;
      const finish = () => {
        // The terminal value is pinned explicitly so the hidden state is
        // exact even where the frame loop never ran (the backstop path).
        collapseProgress.set(1);
        setCollapsed(true);
      };
      if (getPrefersReducedMotion()) {
        finish();
        return;
      }
      const fold = animate(collapseProgress, 1, {
        duration: TRACE_COLLAPSE_DURATION_S,
        ease: TRACE_EASE_IN_QUART,
        onComplete: finish,
      });
      const hideTimer = window.setTimeout(finish, TRACE_COLLAPSE_DURATION_S * 1000);
      return () => {
        fold.stop();
        window.clearTimeout(hideTimer);
      };
    }
    if (!hasCollapsedRef.current) return; // first mount, nothing to undo
    // The composer stayed on the page: show the svg again and re-draw — no
    // wave to wait on, no rise (the mark is already where it rests).
    hasCollapsedRef.current = false;
    setCollapsed(false);
    collapseProgress.set(0);
    const markSettled = () => {
      hasSettledRef.current = true;
    };
    if (getPrefersReducedMotion()) {
      for (const pathLength of pathLengths) pathLength.set(1);
      markSettled();
      return;
    }
    for (const pathLength of pathLengths) pathLength.set(0);
    const stopDraw = runTraceDraw(pathLengths, 0, markSettled);
    const settleTimer = window.setTimeout(markSettled, TRACE_DURATION_S * 1000);
    return () => {
      stopDraw();
      window.clearTimeout(settleTimer);
    };
  }, [submitting, collapseProgress, pathLengths]);

  return {
    hasSettledRef,
    prefersReducedMotion,
    collapsed,
    // Origin: the mark's own centre — the collapse pinches to a point there.
    entranceMotionProps: {
      style: { y, scaleX, scaleY, opacity, originX: 0.5, originY: 0.5 },
    },
    traceStrokes: TRACE_PEN_PIECES.map((piece, index) => ({
      piece,
      pathLength: pathLengths[index]!,
    })),
  };
};
